import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Paper,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Category as CategoryIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { toyTypeService } from '../services/api';
import { ToyType } from '../types';

const GameTypes: React.FC = () => {
  const [gameTypes, setGameTypes] = useState<ToyType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingGameType, setEditingGameType] = useState<ToyType | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const data = await toyTypeService.getAll();
      // Asegurar que data sea un array
      setGameTypes(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Error al cargar los tipos de juguetes');
      console.error('Error fetching toy types:', err);
      // En caso de error, establecer array vacío
      setGameTypes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (gameType?: ToyType) => {
    if (gameType) {
      setEditingGameType(gameType);
      setFormData({
        name: gameType.name,
        description: gameType.description,
        is_active: gameType.is_active,
      });
    } else {
      setEditingGameType(null);
      setFormData({
        name: '',
        description: '',
        is_active: true,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingGameType(null);
  };

  const handleSave = async () => {
    try {
      if (editingGameType) {
        await toyTypeService.update(editingGameType.id, formData);
      } else {
        await toyTypeService.create(formData);
      }
      fetchData();
      handleCloseDialog();
    } catch (err) {
      setError('Error al guardar el tipo de juguete');
      console.error('Error saving toy type:', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este tipo de juguete?')) {
      try {
        await toyTypeService.delete(id);
        fetchData();
      } catch (err) {
        setError('Error al eliminar el tipo de juguete');
        console.error('Error deleting toy type:', err);
      }
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const getStatusColor = (isActive: boolean) => {
    return isActive ? 'success' : 'default';
  };

  const getStatusText = (isActive: boolean) => {
    return isActive ? 'Activo' : 'Inactivo';
  };

  const getStatusIcon = (isActive: boolean) => {
    return isActive ? <CheckIcon /> : <CancelIcon />;
  };

  const getFilteredGameTypes = () => {
    // Asegurar que gameTypes sea un array
    const gameTypesArray = Array.isArray(gameTypes) ? gameTypes : [];
    
    return gameTypesArray.filter(gameType => {
      // Filtro por búsqueda de texto
      const matchesSearch = searchTerm === '' || 
        gameType.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (gameType.description && gameType.description.toLowerCase().includes(searchTerm.toLowerCase()));
      
      return matchesSearch;
    });
  };

  const getStats = () => {
    // Asegurar que gameTypes sea un array
    const typesArray = Array.isArray(gameTypes) ? gameTypes : [];
    const total = typesArray.length;
    const active = typesArray.filter(gt => gt.is_active).length;
    const inactive = typesArray.filter(gt => !gt.is_active).length;

    return { total, active, inactive };
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const stats = getStats();

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ color: 'primary.main' }}>
          Tipos de Juguetes
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{ borderRadius: 2 }}
        >
          Nuevo Tipo
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Búsqueda */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Buscar por nombre o descripción del tipo de juguete..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'primary.main',
                  },
                  '&:hover fieldset': {
                    borderColor: 'primary.main',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                  },
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box display="flex" justifyContent="flex-end" alignItems="center">
              <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
                Mostrando {getFilteredGameTypes().length} de {gameTypes.length} tipos
              </Typography>
              {searchTerm !== '' && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setSearchTerm('')}
                >
                  Limpiar Búsqueda
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Estadísticas */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ textAlign: 'center' }}>
            <CardContent>
              <CategoryIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                {stats.total}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total de Tipos
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ textAlign: 'center' }}>
            <CardContent>
              <CheckIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                {stats.active}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Activos
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ textAlign: 'center' }}>
            <CardContent>
              <CancelIcon sx={{ fontSize: 40, color: 'error.main', mb: 1 }} />
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                {stats.inactive}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Inactivos
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Grid de tarjetas de tipos de juguetes */}
      <Grid container spacing={3}>
        {getFilteredGameTypes().map((gameType) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={gameType.id}>
            <Card 
              sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                position: 'relative',
                transition: 'transform 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4,
                }
              }}
            >
              <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                {/* Badge de estado */}
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Chip
                    icon={getStatusIcon(gameType.is_active)}
                    label={getStatusText(gameType.is_active)}
                    size="small"
                    color={getStatusColor(gameType.is_active) as any}
                    sx={{ fontWeight: 'bold' }}
                  />
                  <Box>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(gameType)}
                      sx={{ mr: 1 }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(gameType.id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>

                {/* Nombre del tipo */}
                <Typography 
                  variant="h6" 
                  component="h3" 
                  sx={{ 
                    fontWeight: 'bold',
                    mb: 2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {gameType.name}
                </Typography>

                {/* Descripción */}
                {gameType.description && (
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ 
                      mb: 2,
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {gameType.description}
                  </Typography>
                )}

                {/* Fechas */}
                <Box sx={{ mt: 'auto' }}>
                  {gameType.created_at && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Creado: {(() => {
                        try {
                          // Crear fecha local sin problemas de zona horaria
                          const [year, month, day] = gameType.created_at.split('T')[0].split('-');
                          const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                          return date.toLocaleDateString();
                        } catch {
                          return 'N/A';
                        }
                      })()}
                    </Typography>
                  )}
                  {gameType.updated_at && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Actualizado: {(() => {
                        try {
                          // Crear fecha local sin problemas de zona horaria
                          const [year, month, day] = gameType.updated_at.split('T')[0].split('-');
                          const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                          return date.toLocaleDateString();
                        } catch {
                          return 'N/A';
                        }
                      })()}
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Mensaje si no hay tipos de juguetes */}
      {Array.isArray(gameTypes) && gameTypes.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center', mt: 3 }}>
          <CategoryIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No hay tipos de juguetes registrados
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Comienza agregando tu primer tipo de juguete
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Agregar Primer Tipo
          </Button>
        </Paper>
      )}

      {/* Dialog para crear/editar tipo de juguete */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingGameType ? 'Editar Tipo de Juguete' : 'Nuevo Tipo de Juguete'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Información básica */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                Información del Tipo
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nombre del Tipo"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                sx={{ mt: 2, mb: 1 }}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descripción (Opcional)"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                multiline
                rows={4}
                sx={{ mt: 2, mb: 1 }}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>

            {/* Estado */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                Estado
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => handleInputChange('is_active', e.target.checked)}
                    color="success"
                  />
                }
                label={
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {formData.is_active ? 'Activo' : 'Inactivo'}
                  </Typography>
                }
                sx={{ mt: 2 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained">
            {editingGameType ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GameTypes;
